<template>
  <div class="tracker-card">
    <div
      class="tracker-card__wrapper"
      :class="{ 'not-subscribe': notSubscribe }"
    >
      <div class="tracker-card__date">
        <Date :text="date" />
      </div>
      <div class="tracker-card__header">
        <nuxt-link v-if="hasAccess" :to="link" target="_blank">{{
          title
        }}</nuxt-link>
        <nuxt-link v-else class="unauthorised" :to="'#'">{{
          noAccessShort
        }}</nuxt-link>

        <button
          class="tracker-card__header-direction"
          :class="{ open: showMore }"
          @click="getMoreInfo(item.nid)"
        >
          <Icon :icon="'arrow-red'" />
        </button>
      </div>
      <div
        class="tracker-card__content section-border--top section-border--right section-border--bottom"
      >
        <div
          v-if="item.documentTypes && item.documentTypes.length"
          class="tracker-card__item-row"
        >
          <div class="tracker-card__item-title">Document type</div>
          <div class="tracker-card__item-value">
            <span
              v-for="documentType in item.documentTypes"
              :key="documentType.tid"
              >{{ documentType.title }},
            </span>
          </div>
        </div>
        <div v-if="item.datePublished" class="tracker-card__item-row">
          <div class="tracker-card__item-title">Date published</div>
          <div class="tracker-card__item-value">
            <Date :text="item.datePublished" />
          </div>
        </div>
        <div v-if="item.effectiveDate" class="tracker-card__item-row">
          <div class="tracker-card__item-title">Effective date</div>
          <div class="tracker-card__item-value">
            <Date :text="item.effectiveDate" />
          </div>
        </div>
        <div v-if="item.responseDeadline" class="tracker-card__item-row">
          <div class="tracker-card__item-title">Response deadline</div>
          <div class="tracker-card__item-value">
            <Date :text="item.responseDeadline" />
          </div>
        </div>
        <div
          v-if="item.companies && item.companies.length"
          class="tracker-card__item-row"
        >
          <div class="tracker-card__item-title">Company</div>
          <div class="tracker-card__item-value">
            <span v-for="company in item.companies" :key="company.tid"
              >{{ company.title }},
            </span>
          </div>
        </div>
        <div v-if="item.authorities" class="tracker-card__item-row">
          <div class="tracker-card__item-title">Authority</div>
          <div v-if="hasAccess" class="tracker-card__item-value">
            <span v-for="authority in item.authorities" :key="authority.tid"
              >{{ authority.title }},
            </span>
          </div>
          <div v-else class="tracker-card__item-value">
            <span class="unauthorised">{{ noAccessShort }}</span>
          </div>
        </div>
        <div class="tracker-card__item-wrap">
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="hasAccess"
            class="tracker-card__item-text"
            v-html="item.body"
          />
          <!-- eslint-enable vue/no-v-html -->
          <div v-else class="tracker-card__item-text unauthorised">
            {{ noAccessShort }}
          </div>
        </div>
        <div class="tracker-card__item-link">
          <nuxt-link :to="link" target="_blank"> Read more </nuxt-link>
        </div>
        <div v-if="showMore" class="tracker-card__item-hide">
          <div v-if="parents" class="tracker-card__item-parents">
            <div
              v-for="(parent, index) in parents"
              :key="index"
              class="tracker-card__item-parent tracker-card__item-child"
            >
              <Date :text="parent.created" />
              <nuxt-link :to="parent.view_node">
                {{ parent.title }}
              </nuxt-link>
            </div>
          </div>
        </div>
      </div>
      <Tooltip v-if="notSubscribe" />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { noAccessShort } from "@/utils/constants";

export default {
  name: "TrackerCard",
  components: {
    Date: () => import("../../components/elements/Date"),
    Icon: () => import("../../components/elements/Icon"),
    // NoteButton: () => import("../../components/elements/NoteButton"),
    Tooltip: () => import("../elements/Tooltip"),
  },
  props: {
    date: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    link: {
      type: String,
      default: null,
    },
    item: {
      type: Object,
      default: null,
    },
    hasAccess: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showMore: false,
      parents: null,
      loading: false,
      notSubscribe: false,
      noAccessShort,
    };
  },
  computed: {
    ...mapState("Profile", ["userAccess"]),
  },
  methods: {
    getMoreInfo() {
      this.showMore = !this.showMore;
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/styles/helpers/variables";
@import "../../assets/styles/helpers/mixin";

.tracker-card {
  position: relative;

  &__wrapper {
    position: relative;
  }

  &:after {
    position: absolute;
    content: "";
    width: 1.6rem;
    height: 1.6rem;
    border: 2px solid $black;
    background: $accentBlue;
    border-radius: 50%;
    left: -4rem;
    top: 1rem;
  }

  &:before {
    position: absolute;
    content: "";
    width: 2rem;
    height: 1px;
    border-bottom: 1px solid $border;
    left: -6rem;
    top: 1.8rem;
  }

  &__header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 1rem;
    position: relative;

    &-count {
      position: absolute;
      right: 5%;
      bottom: -0.5rem;

      .btn-note {
        cursor: default !important;
      }
    }

    a {
      font-size: 2.1rem;
      line-height: 2.7rem;
      color: $link-primary;
      text-decoration: none;
      transition: 0.3s ease-out;
      width: 90%;
      font-weight: 700;

      &:hover {
        color: $link-primary-hover;
        text-decoration: underline;
      }
    }

    &-direction {
      width: 5%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;

      .icon {
        transition: 0.3s ease-out;
        transform: rotate(-90deg);
      }

      &.open {
        .icon {
          transform: rotate(90deg);
        }
      }
    }
  }

  &__content {
    padding: 1.6rem 6rem 1.6rem 0;
  }

  &__item {
    &-row {
      display: flex;

      .date {
        font-size: 1.6rem !important;
      }
    }

    &-title {
      width: 20%;
      font-weight: 700;
      font-size: 1.4rem;
      margin-right: 3rem;
    }

    &-text {
      margin-top: 3rem;
      font-size: 1.6rem;
    }

    &-link {
      a {
        color: $red;
        text-decoration: none;
        font-size: 1.4rem;
        font-weight: 600;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    &-parent {
      margin-top: 3rem;

      a {
        font-size: 1.6rem;
        font-weight: 600;
        text-decoration: none;
        line-height: 2.1rem;
        transition: 0.3s ease-out;
        color: $text_gray;

        &:hover {
          color: $brightGreen;
        }
      }
    }

    &-child {
      position: relative;

      &:after {
        position: absolute;
        content: "";
        width: 1.6rem;
        height: 1.6rem;
        border: 2px solid $black;
        background: #f20000;
        border-radius: 50%;
        left: -4rem;
        top: 1rem;
      }

      &:before {
        position: absolute;
        content: "";
        width: 2rem;
        height: 1px;
        border-bottom: 1px solid $border;
        left: -6rem;
        top: 1.8rem;
      }
    }

    &-value {
      max-width: calc(80% - 3rem);
    }
  }
}
</style>
